import React, { useState } from "react";
import { X, Link as LinkIcon, File, Send, Plus, Trash2, Maximize2, Minimize2 } from "lucide-react";

const TaskSubmissionForm = ({ task, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    message: "",
    links: [""],
    files: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleLinkChange = (index, value) => {
    const updatedLinks = [...formData.links];
    updatedLinks[index] = value;
    setFormData({
      ...formData,
      links: updatedLinks,
    });
  };

  const addLinkField = () => {
    setFormData({
      ...formData,
      links: [...formData.links, ""],
    });
  };

  const removeLinkField = (index) => {
    const updatedLinks = formData.links.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      links: updatedLinks,
    });
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.message.trim()) {
      setError("Please provide a description of your work");
      return;
    }

    // Filter out empty links
    const links = formData.links.filter((link) => link.trim() !== "");

    // Check if at least one link is provided
    if (links.length === 0) {
      setError("Please provide at least one link to your work");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // In a real implementation, you would handle file uploads here
      // For now, we'll just submit the links and message
      await onSubmit({
        message: formData.message,
        links: links,
        // In a full implementation, we would include file references here
      });
      
      // No need to update status here - the API should handle this
      // The API should change status to 'submitted' when work is submitted
      
      onClose();
    } catch (err) {
      setError(err.message || "Failed to submit work");
    } finally {
      setLoading(false);
    }
  };

  // Handle backdrop click to close the modal
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  return (
    <div 
      className={`fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center ${isFullscreen ? 'p-0' : 'p-4'}`}
      onClick={handleBackdropClick}
    >
      <div className={`bg-white rounded-xl shadow-2xl ${isFullscreen ? 'w-full h-full rounded-none' : 'w-full max-w-3xl'} animate-fadeIn`} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-gray-200 bg-gray-50 rounded-t-xl relative">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <button
                onClick={toggleFullscreen}
                className="mr-3 p-1 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
              >
                {isFullscreen ? 
                  <Minimize2 className="h-5 w-5" /> : 
                  <Maximize2 className="h-5 w-5" />
                }
              </button>
              <h2 className="text-xl font-bold text-gray-800">Submit Work for: {task.title}</h2>
            </div>
            <button 
              type="button"
              onClick={onClose} 
              className="text-gray-500 hover:text-gray-700 bg-white hover:bg-gray-100 rounded-full p-2 transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer z-50 shadow-sm"
              aria-label="Close"
              disabled={loading}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable content area */}
        <div className={`p-6 ${isFullscreen ? 'overflow-y-auto h-[calc(100vh-14rem)]' : 'max-h-[70vh] overflow-y-auto'} custom-scrollbar`}>
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description of Your Work <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="6"
                  className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 shadow-sm"
                  placeholder="Describe what you've done, any challenges you faced, and any notes for the reviewer..."
                  required
                ></textarea>
                <p className="text-xs text-gray-500 mt-2">
                  Provide context about your submission to help the reviewer understand your work
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Links (GitHub, Figma, Drive, etc.) <span className="text-red-500">*</span>
                </label>
                {formData.links.map((link, index) => (
                  <div key={index} className="flex mb-3">
                    <div className="relative flex-grow">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <LinkIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="url"
                        value={link}
                        onChange={(e) => handleLinkChange(index, e.target.value)}
                        className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 shadow-sm"
                        placeholder="https://..."
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeLinkField(index)}
                      className="bg-gray-100 border border-gray-300 text-gray-600 px-3 rounded-r-lg hover:bg-gray-200 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addLinkField}
                  className="mt-2 flex items-center text-indigo-600 hover:text-indigo-800 transition-colors"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Another Link
                </button>
                <p className="text-xs text-gray-500 mt-2">
                  Please add links to your work (e.g., GitHub repository, deployed site, design files)
                </p>
              </div>
            </div>
          </form>
        </div>

        {/* Fixed footer with action buttons */}
        <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 font-medium shadow-sm transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium flex items-center shadow-sm transition-colors"
            disabled={loading}
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Submit Work
              </>
            )}
          </button>
        </div>
      </div>

      {/* Add CSS for custom scrollbar */}
      <style jsx global>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.98); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default TaskSubmissionForm;
